/**
 * router/index.ts
 *
 * Automatic routes for `./src/pages/*.vue`
 */

import { createRouter, createWebHistory } from "vue-router";
import { ROUTE } from "@/router/routeDefinitions.ts";
import { PERMISSIONS_DOCUMENT_DOWNLOAD } from "@/config";
import { safeLocalStorage } from "@polargold/pg-frontend-core";

const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    routes: [
        {
            path: ROUTE.PUBLIC_STARTPAGE.PATH,
            name: ROUTE.PUBLIC_STARTPAGE.NAME,
            component: () => import("@/views/PublicViews/PublicStartpage/PublicStartpage.vue"),
        },
        {
            path: ROUTE.PUBLIC_ERROR_PAGE.PATH,
            name: ROUTE.PUBLIC_ERROR_PAGE.NAME,
            component: () => import("@/views/PublicViews/ErrorPage/ErrorPage.vue"),
        },
        {
            path: ROUTE.RESTRICTED_AREA.PATH,
            name: ROUTE.RESTRICTED_AREA.NAME,
            redirect: { name: ROUTE.RESTRICTED_DOCUMENTS_LIST.NAME },
            component: () => import("@/views/RestrictedViews/RestrictedViews.vue"),
            children: [
                {
                    path: ROUTE.RESTRICTED_DOCUMENTS_LIST.PATH,
                    name: ROUTE.RESTRICTED_DOCUMENTS_LIST.NAME,
                    component: () => import("@/views/RestrictedViews/DocumentsList/DocumentsList.vue"),
                },
                {
                    path: ROUTE.RESTRICTED_DOCUMENTS_DOWNLOAD.PATH,
                    name: ROUTE.RESTRICTED_DOCUMENTS_DOWNLOAD.NAME,
                    component: () => import("@/views/RestrictedViews/DocumentsDownload/DocumentsDownload.vue"),
                    meta: { hasAnyPermission: PERMISSIONS_DOCUMENT_DOWNLOAD },
                    children: [
                        {
                            path: ROUTE.RESTRICTED_DOCUMENTS_DOWNLOAD_LIST.PATH,
                            name: ROUTE.RESTRICTED_DOCUMENTS_DOWNLOAD_LIST.NAME,
                            component: () => import("@/views/RestrictedViews/DocumentsDownload/DocumentsDownloadList/DocumentsDownloadList.vue")
                        },
                        {
                            path: ROUTE.RESTRICTED_DOCUMENTS_DOWNLOAD_SEND_MAIL.PATH,
                            name: ROUTE.RESTRICTED_DOCUMENTS_DOWNLOAD_SEND_MAIL.NAME,
                            component: () => import("@/views/RestrictedViews/DocumentsDownload/DocumentsSendMail/DocumentsSendMail.vue")
                        },
                        {
                            path: ROUTE.RESTRICTED_DOCUMENTS_DOWNLOAD_SEND_MAIL_SUCCESS.PATH,
                            name: ROUTE.RESTRICTED_DOCUMENTS_DOWNLOAD_SEND_MAIL_SUCCESS.NAME,
                            component: () => import("@/views/RestrictedViews/DocumentsDownload/DocumentsSendMailSuccess/DocumentsSendMailSuccess.vue")
                        },
                        {
                            path: ROUTE.RESTRICTED_DOCUMENTS_DOWNLOAD_SEND_MAIL_FAILURE.PATH,
                            name: ROUTE.RESTRICTED_DOCUMENTS_DOWNLOAD_SEND_MAIL_FAILURE.NAME,
                            component: () => import("@/views/RestrictedViews/DocumentsDownload/DocumentsSendMailFailure/DocumentsSendMailFailure.vue")
                        },
                    ]
                }
            ]
        },
        {
            path: ROUTE.AUTHENTICATION_CALLBACK.PATH,
            name: ROUTE.AUTHENTICATION_CALLBACK.NAME,
            component: () => import("@/views/Authentication/AuthenticationCallback.vue"),
        },
        {
            path: ROUTE.PUBLIC_PAGE_NOT_ALLOWED.PATH,
            name: ROUTE.PUBLIC_PAGE_NOT_ALLOWED.NAME,
            component: () => import("@/views/PublicViews/PageNotAllowed/PageNotAllowed.vue"),
        },
        {
            path: ROUTE.PUBLIC_UPTIME_STATUS.PATH,
            name: ROUTE.PUBLIC_UPTIME_STATUS.NAME,
            component: () => import("@/views/PublicViews/PublicUptimeStatus/PublicUptimeStatus.vue"),
        },
        {
            path: "/:pathMatch(.*)*",
            name: ROUTE.PUBLIC_PAGE_NOT_FOUND.NAME,
            component: () => import("@/views/PublicViews/PageNotFound/PageNotFound.vue"),
        }
    ],
});

router.onError((err, to) => {
    if (err?.message?.includes?.("Failed to fetch dynamically imported module")) {
        if (safeLocalStorage.getItem("vuetify:dynamic-reload")) {
            console.error("Dynamic import error, reloading page did not fix it", err);
        } else {
            console.log("Reloading page to fix dynamic import error");

            safeLocalStorage.setItem("vuetify:dynamic-reload", "true");

            location.assign(to.fullPath);
        }
    } else {
        console.error(err);
    }
});

router.isReady().then(() => {
    safeLocalStorage.removeItem("vuetify:dynamic-reload");
});

export default router;
